import type { ChannelClient } from '@hibro/platform-core';
import type {
  IPermissionService,
  PermissionPrompt as Prompt,
} from '@hibro/shared';
import {
  PERMISSION_PROMPT_EVENT,
  PERMISSION_RESOLVED_EVENT,
} from '@hibro/shared';
import { ConfirmDialog } from '@hibro/ui-client';
import { type ReactNode, useEffect, useState } from 'react';

import { useT } from '../i18n/i18n';

export interface PermissionPromptProps {
  permissionService: IPermissionService;
  /** Channel client used to receive broker-pushed prompts (架构方案 §5.6). */
  client: ChannelClient;
  /** Reports whether the permission dialog obscures native editor content. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Authorization prompt host (架构方案 §5.6). Listens for broker-pushed
 * `PERMISSION_PROMPT_EVENT`s, shows an Allow/Deny dialog for the oldest pending
 * request, and answers via `permissionService.respond`. A `PERMISSION_RESOLVED_EVENT`
 * (e.g. another window answered first) dismisses the matching request here too.
 * Mounted persistently by the Workbench so prompts can appear at any time.
 */
export function PermissionPrompt({
  permissionService,
  client,
  onOpenChange,
}: PermissionPromptProps): ReactNode {
  const t = useT();
  const [queue, setQueue] = useState<Prompt[]>([]);

  useEffect(() => {
    const disposePrompt = client.listen(PERMISSION_PROMPT_EVENT, (data) => {
      const prompt = data as Prompt;
      setQueue((prev) =>
        prev.some((item) => item.id === prompt.id) ? prev : [...prev, prompt],
      );
    });
    const disposeResolved = client.listen(PERMISSION_RESOLVED_EVENT, (data) => {
      const { id } = data as { id: string };
      setQueue((prev) => prev.filter((item) => item.id !== id));
    });
    return () => {
      disposePrompt();
      disposeResolved();
    };
  }, [client]);

  const current = queue[0];
  useEffect(() => {
    onOpenChange?.(current !== undefined);
  }, [current, onOpenChange]);
  if (!current) {
    return null;
  }

  const answer = (granted: boolean): void => {
    // Optimistically dequeue; the resolved broadcast also dequeues other windows.
    setQueue((prev) => prev.filter((item) => item.id !== current.id));
    permissionService.respond(current.id, granted).catch(() => undefined);
  };

  // Reuse the shared declarative confirm (架构方案 §5.8 声明档): allow = confirm,
  // deny = cancel; Esc / click-away also counts as a deny (onCancel).
  return (
    <ConfirmDialog
      open
      title={t('permission.prompt.title')}
      message={t('permission.prompt.body', {
        moduleId: current.moduleId,
        permission: current.permission,
      })}
      confirmText={t('permission.prompt.allow')}
      cancelText={t('permission.prompt.deny')}
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    />
  );
}
